import { supabase } from './supabase';
import type { Contact } from '../types';

export async function addContact(userId: string, contact: Omit<Contact, 'id'>) {
  const { data, error } = await supabase
    .from('contacts')
    .insert([{ ...contact, user_id: userId }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateContact(contactId: string, data: Partial<Contact>) {
  const { error } = await supabase
    .from('contacts')
    .update(data)
    .eq('id', contactId);

  if (error) throw error;
}

export async function getContacts(userId: string): Promise<Contact[]> {
  const { data, error } = await supabase
    .from('contacts')
    .select('*')
    .eq('user_id', userId);

  if (error) throw error;
  return data;
}

export async function deleteContact(contactId: string) {
  const { error } = await supabase
    .from('contacts')
    .delete()
    .eq('id', contactId);

  if (error) throw error;
}

interface CardOrderResponse {
  id: string;
  lobOrderId: string;
  status: string;
}

export async function createOrder(data: {
  contactId?: string;
  message: string;
  occasion: string;
  imageUrl: string;
  to: {
    name: string;
    address_line1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
    address_country: string;
  };
  front: string;
  back: string;
  useRemoteUrls?: boolean;
  description?: string;
  size?: string;
}): Promise<CardOrderResponse> {
  const { data: response, error } = await supabase.functions.invoke('create-lob-order', {
    body: data
  });
  
  if (error) throw error;
  return response;
}

interface UserProfile {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  signature_url: string | null;
  default_message?: string;
  address_line1?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
}

export async function getProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGNF') throw error;
  return data;
}

export async function updateProfile(userId: string, updates: Partial<UserProfile>) {
  const { error } = await supabase
    .from('users')
    .upsert({ user_id: userId, ...updates })
    .eq('user_id', userId);

  if (error) throw error;
}

export async function uploadSignature(userId: string, signatureBlob: Blob) {
  const fileExt = 'png';
  const filePath = `signatures/${userId}/signature.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('signatures')
    .upload(filePath, signatureBlob, {
      upsert: true,
      contentType: 'image/png'
    });

  if (uploadError) throw uploadError;

  const { data: { publicUrl } } = supabase.storage
    .from('signatures')
    .getPublicUrl(filePath);

  await updateProfile(userId, { signature_url: publicUrl });
  return publicUrl;
} 